// The template library, put in an order a coach can find things in.
//
// The library is in two parts.
//
//   The core set: one template for every scenario a new client can describe at sign-up (gym or
//   home, days a week, level, goal). These are what auto-assign hands out. They are grouped by where
//   the client trains and what they are after, and within a group ordered by days a week and then
//   level.
//
//   Extra programs: every other template. Nobody is given one automatically; a coach assigns one by
//   hand. They are kept apart from the core set, in a folder of their own, by name.
//
// An archived program is one that has been withdrawn. It is never listed.

// "[Home ]Family: split (N Day / Level)". api/lib/matchProgram.js recognises a core template by the
// same pattern; src/utils/templateLibrary.spec.js checks the two agree on the real library.
const CORE_NAME = /^(Home )?(Build|Burn|Strength|Fit): .+ \((\d) Day \/ (Beginner|Intermediate|Advanced)\)$/;

const PLACES = ['Gym', 'Home'];
const FAMILIES = ['Build', 'Strength', 'Burn', 'Fit'];
const LEVELS = ['Beginner', 'Intermediate', 'Advanced'];
// What each family is for, in the words a client uses at sign-up.
export const FAMILY_LABELS = { Build: 'Build muscle', Strength: 'Get stronger', Burn: 'Fat loss', Fit: 'General fitness' };
export const EXTRA_PROGRAMS_LABEL = 'Extra programs';

export const isArchived = (program) => !!program?.archived_at;

/**
 * What a core template is for: { place, family, days, level }, or null for any other program.
 *
 * A core template is the one auto-assign hands a new client: shared (it has no owner), not
 * archived, and named for its scenario. A coach's own program with a name of the same shape is not
 * one.
 */
export const coreScenario = (program) => {
  if (!program || program.created_by || isArchived(program)) return null;
  const m = CORE_NAME.exec(program.name || '');
  if (!m) return null;
  return { place: m[1] ? 'Home' : 'Gym', family: m[2], days: Number(m[3]), level: m[4] };
};

/**
 * templates  rows from `programs` that belong in the template library
 *
 * Returns { core, extra }.
 *   core   [{ key, label, programs }]: one group for each place and family that has a core
 *          template, in a fixed order. Empty groups are left out.
 *   extra  every other template, by name.
 * Archived templates are dropped from both.
 */
export const splitTemplateLibrary = (templates) => {
  const found = new Map();
  const extra = [];

  for (const program of templates || []) {
    if (isArchived(program)) continue;
    const scenario = coreScenario(program);
    if (!scenario) { extra.push(program); continue; }
    const key = `${scenario.place}-${scenario.family}`;
    if (!found.has(key)) found.set(key, []);
    found.get(key).push({ program, scenario });
  }

  const core = [];
  for (const place of PLACES) {
    for (const family of FAMILIES) {
      const group = found.get(`${place}-${family}`);
      if (!group) continue;
      group.sort((a, b) =>
        a.scenario.days - b.scenario.days
        || LEVELS.indexOf(a.scenario.level) - LEVELS.indexOf(b.scenario.level)
        || a.program.id - b.program.id);
      core.push({ key: `${place}-${family}`.toLowerCase(), label: `${place} · ${FAMILY_LABELS[family]}`, programs: group.map((x) => x.program) });
    }
  }
  extra.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'en', { numeric: true }) || a.id - b.id);
  return { core, extra };
};

/**
 * The whole library as one list of groups: the core groups, then "Extra programs" if there are any.
 * For a list that has no folders of its own, such as a dropdown.
 */
export const groupTemplateLibrary = (templates) => {
  const { core, extra } = splitTemplateLibrary(templates);
  return extra.length > 0 ? [...core, { key: 'extra', label: EXTRA_PROGRAMS_LABEL, programs: extra }] : core;
};
